import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';
import logo from './assets/logo.png';

function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-page">
      <nav className="privacy-nav">
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Link to="/">
                <img src={logo} alt="LucidBlack" className="logo-image" />
              </Link>
            </div>
            <Link to="/" className="btn-text">
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      <div className="privacy-container">
        <div className="container">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-update">Last Updated: October 27, 2025</p>

          <div className="privacy-content">
            <section className="privacy-section">
              <h2>I. Introduction and Scope</h2>
              <p>
                This Privacy Policy describes how [Your Company Legal Name] (referred to as "Lucid Black," "we," "us," or "our")
                collects, uses, shares, and protects the personal information you provide when you use the lucidblack.io website
                (the "Site") and apply for or use the Lucid Black Rewards Credit Card (the "Card").
              </p>
              <p>
                This policy applies to all prospective and current Cardholders and Site visitors. By using our Site or services,
                you consent to the data practices described in this policy.
              </p>
            </section>

            <section className="privacy-section">
              <h2>II. Information We Collect</h2>
              <p>Because the Lucid Black Card is a financial product, we collect and process highly sensitive information.</p>

              <h3>A. Information You Provide Directly</h3>
              <p>This includes information you provide when applying for the Card, managing your account, or contacting customer service:</p>
              <ul>
                <li>
                  <strong>Identity Data:</strong> Full legal name, date of birth, Social Security Number (SSN), driver's license
                  or other government-issued ID.
                </li>
                <li>
                  <strong>Contact Data:</strong> Postal address, email address, and phone number.
                </li>
                <li>
                  <strong>Financial Data:</strong> Income information, employment details, bank account numbers, and specific details
                  regarding your eligible Vehicle Loan (e.g., lender, outstanding balance, monthly payment amount).
                </li>
                <li>
                  <strong>Account Credentials:</strong> Usernames and passwords used to access your Lucid Black account.
                </li>
              </ul>

              <h3>B. Information Collected from Third Parties</h3>
              <p>We obtain information from outside sources necessary for processing your application and maintaining your account:</p>
              <ul>
                <li>
                  <strong>Credit Reporting Agencies:</strong> Your credit score, credit history, and other data used to determine
                  eligibility for the Card.
                </li>
                <li>
                  <strong>Vehicle Loan Verification:</strong> Confirmation and details of your active vehicle loan from your lender
                  or loan servicer.
                </li>
                <li>
                  <strong>Identity Verification:</strong> Data from third-party services to verify your identity and prevent fraud.
                </li>
              </ul>

              <h3>C. Information Collected Automatically (Cookies & Tracking)</h3>
              <p>When you visit our Site, we automatically collect:</p>
              <ul>
                <li>
                  <strong>Usage Data:</strong> IP address, browser type, operating system, pages visited, and time spent on the Site.
                </li>
                <li>
                  <strong>Geolocation Data:</strong> General location data based on your IP address.
                </li>
                <li>
                  <strong>Tracking Data:</strong> Information collected via cookies, pixel tags, and similar technologies to analyze
                  Site performance and deliver relevant advertisements.
                </li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>III. How We Use Your Information</h2>
              <p>We use your personal information for the following primary purposes:</p>

              <div className="privacy-table">
                <div className="table-row table-header">
                  <div className="table-cell">Purpose</div>
                  <div className="table-cell">Description (Legal Basis)</div>
                </div>
                <div className="table-row">
                  <div className="table-cell"><strong>Service Provision</strong></div>
                  <div className="table-cell">
                    To process your Card application, establish your account, and calculate and apply your rewards points
                    for car loan payments.
                  </div>
                </div>
                <div className="table-row">
                  <div className="table-cell"><strong>Compliance & Legal</strong></div>
                  <div className="table-cell">
                    To comply with federal and state regulations, including the Gramm-Leach-Bliley Act (GLBA), and to prevent
                    fraud, money laundering, and identity theft.
                  </div>
                </div>
                <div className="table-row">
                  <div className="table-cell"><strong>Customer Support</strong></div>
                  <div className="table-cell">
                    To respond to your inquiries, provide account statements, and manage disputes.
                  </div>
                </div>
                <div className="table-row">
                  <div className="table-cell"><strong>Marketing</strong></div>
                  <div className="table-cell">
                    To send you promotional communications about the Card and related products, subject to your right to
                    Opt-Out (see Section V).
                  </div>
                </div>
                <div className="table-row">
                  <div className="table-cell"><strong>Improvement</strong></div>
                  <div className="table-cell">
                    To analyze Site and Card usage to improve our products, services, and security.
                  </div>
                </div>
              </div>
            </section>

            <section className="privacy-section">
              <h2>IV. Sharing and Disclosure of Your Information</h2>
              <p>We do not sell your personal information in the traditional sense, but we may share it with the following parties:</p>
              <ul>
                <li>
                  <strong>Financial Partners:</strong> The issuing bank, payment networks (e.g., Visa/Mastercard), and other
                  entities required to process transactions and service your account.
                </li>
                <li>
                  <strong>Service Providers:</strong> Third-party companies that perform services on our behalf, such as customer
                  support, data hosting, and analytics. These providers are required to protect your information by contract.
                </li>
                <li>
                  <strong>Vehicle Loan Servicers:</strong> To verify your payments and calculate eligible rewards.
                </li>
                <li>
                  <strong>Legal & Regulatory:</strong> When required by law, subpoena, or to cooperate with government investigations.
                </li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>V. Your Privacy Rights and Choices</h2>
              <p>
                You have certain rights regarding your personal information. To exercise any of these rights, please contact us
                using the information in Section VII.
              </p>
              <ul>
                <li>
                  <strong>Right to Access:</strong> You have the right to request the specific pieces and categories of personal
                  information we have collected about you.
                </li>
                <li>
                  <strong>Right to Correct/Rectify:</strong> You have the right to correct inaccurate or incomplete personal
                  information we hold about you.
                </li>
                <li>
                  <strong>Right to Limit Sharing/Opt-Out:</strong>
                  <ul className="nested-list">
                    <li>
                      <strong>Financial Information (GLBA Opt-Out):</strong> You have the right to direct us not to share certain
                      nonpublic personal information with non-affiliates for their marketing purposes.
                    </li>
                    <li>
                      <strong>Marketing Communications:</strong> You can opt-out of receiving marketing emails from us by following
                      the unsubscribe link in those emails.
                    </li>
                  </ul>
                </li>
              </ul>
            </section>

            <section className="privacy-section">
              <h2>VI. Data Security and Retention</h2>
              <p>
                <strong>Security:</strong> We maintain reasonable and appropriate administrative, technical, and physical safeguards
                designed to protect your personal information from unauthorized access, use, or disclosure, including [mention one
                specific safeguard, e.g., encryption, multi-factor authentication].
              </p>
              <p>
                <strong>Retention:</strong> We retain your personal information for as long as necessary to fulfill the purposes
                outlined in this policy, service your account, or as required by financial record-keeping laws and regulations
                (e.g., [mention a time frame, e.g., seven years]).
              </p>
            </section>

            <section className="privacy-section">
              <h2>VII. Contact Us</h2>
              <p>If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:</p>
              <div className="contact-info">
                <p><strong>Email:</strong> privacy@lucidblack.io</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <footer className="privacy-footer">
        <div className="container">
          <p>&copy; 2026 LucidBlack. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Privacy;
